/**
 * Interface for route data used in Angular router configurations
 * Ensures type safety for breadcrumb, navigation, and context management
 */
export interface RouteData {
  /** The breadcrumb name displayed in navigation */
  breadcrumb: string;
  /** The initial path for context management */
  initialPath: string;
  /** The navigation mode: 'browser', 'add', or 'edit' */
  mode: "browser" | "add" | "edit";
  /** The initial breadcrumb name for context creation */
  initialBreadcrumbName: string;
  /** The browser tab title (optional) */
  title?: string;
}

/**
 * Converts a string to Capitalize format (PascalCase)
 * @param str - The string to convert
 * @returns The string in Capitalize format
 */
function toCapitalize(str: string): string {
  return str
    .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
    .replace(/^./, (char) => char.toUpperCase());
}

/**
 * Helper function to create typed route data with convenient parameter order
 * Provides type safety and IntelliSense for route configurations
 *
 * @param initialPath - The initial path for context management (required)
 * @param breadcrumb - The breadcrumb name displayed in navigation (optional, defaults to initialPath in camelCase)
 * @param mode - The navigation mode (optional, defaults to 'browser')
 * @param initialBreadcrumbName - The initial breadcrumb name (optional, defaults to breadcrumb)
 * @param title - The browser tab title (optional)
 * @returns The typed route data object
 */
export function createRouteData(
  initialPath: string,
  breadcrumb: string = toCapitalize(initialPath),
  mode: "browser" | "add" | "edit" = "browser",
  initialBreadcrumbName?: string,
  title = breadcrumb,
): RouteData {
  return {
    initialPath,
    breadcrumb,
    mode,
    initialBreadcrumbName: initialBreadcrumbName ?? breadcrumb,
    title,
  };
}
